/**
 * Broadcasts — the record of what was sent to many clients, and writing the
 * next one (FR-E9). Redrawn on the list shell in Wave 15E (C14 §10.6; ADR-0033):
 * what was sent is the list, and writing one is a dialog (`ComposeDialog`).
 * It is not an edit page: nothing is saved until it is sent, and a send is not
 * a save — so no `EditBar`.
 *
 * **Two locks before a send.** The first is the audience preview in the
 * dialog: *Send* stays disabled until the audience has been resolved and shown
 * by name. The second is a `ConfirmDialog` that repeats who and how many
 * (ADR-0033 §1: anything that sends asks first). A broadcast cannot be
 * recalled.
 *
 * **The preview is invalidated the moment the audience changes.** A count from
 * the previous filter beside a send button is the confirmation lying.
 *
 * ## Why "in their portal" is not "failed"
 *
 * A recipient whose channel send was refused still has the message where they
 * read things. The service counts that as `posted` and this screen says so.
 * Calling it a failure would have a firm re-sending a message its clients
 * already have — which is the one outcome worse than not sending it.
 */

import { Alert, Badge, Button, ConfirmDialog, PageHeader, formatDateTime } from '@fiveox/ui'
import { Megaphone } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  useListTable,
} from '../../components/page'
import { CLIENT, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type AudiencePreview,
  type Broadcast,
  DELIVERY_LABELS,
  audienceSummary,
  broadcasts,
  canSend,
  deliveryBreakdown,
} from '../../lib/api/chat'
import { type Service, services } from '../../lib/api/services'
import { type BroadcastDraft, ComposeDialog, EMPTY_DRAFT } from './ComposeDialog'

export function Compose() {
  const { can } = useEvaluation()
  const allowed = can(CLIENT, 'can_view_all')

  const [catalog, setCatalog] = useState<Service[]>([])
  const [sent, setSent] = useState<Broadcast[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [draft, setDraft] = useState<BroadcastDraft>(EMPTY_DRAFT)
  const [preview, setPreview] = useState<AudiencePreview | null>(null)
  const [composing, setComposing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [sending, setSending] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  useEffect(() => {
    if (!allowed) return
    // Read to confirm the surface is reachable at all; the screen's own gate is
    // `can(CLIENT, 'can_view_all')`, which is the same answer the service
    // enforces with.
    void broadcasts.vocabulary().catch(() => setError('Your permissions could not be read.'))
    void services
      .list()
      .then((b) => setCatalog(b.data.services))
      .catch(() => undefined)
    broadcasts
      .list()
      .then((b) => setSent(b.data))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'What you have sent could not be loaded.'),
      )
      .finally(() => setLoading(false))
  }, [allowed])

  // **The preview is invalidated the moment the filter changes.**
  const audienceKey = JSON.stringify(draft.audience)
  useEffect(() => {
    setPreview(null)
  }, [audienceKey])

  const resolve = async () => {
    setResolving(true)
    setDialogError(null)
    try {
      const body = await broadcasts.audience(draft.audience)
      setPreview(body.data)
    } catch (err) {
      setPreview(null)
      setDialogError(err instanceof ApiError ? err.message : 'That audience could not be read.')
    } finally {
      setResolving(false)
    }
  }

  const send = async () => {
    setSending(true)
    setDialogError(null)
    try {
      const body = await broadcasts.send({
        subject: draft.subject,
        body: draft.body,
        audience: draft.audience,
        template_code: draft.templateCode || undefined,
      })
      const out = body.data
      setResult(`Sent to ${out.broadcast.recipient_count}. ${deliveryBreakdown(out.broadcast)}.`)
      setDraft(EMPTY_DRAFT)
      setPreview(null)
      setConfirming(false)
      const listed = await broadcasts.list()
      setSent(listed.data)
    } catch (err) {
      setDialogError(err instanceof ApiError ? err.message : 'The broadcast could not be sent.')
    } finally {
      setSending(false)
    }
  }

  const columns = useMemo<ListColumn<Broadcast>[]>(
    () => [
      {
        key: 'subject',
        label: 'Subject',
        width: 260,
        required: true,
        phone: true,
        sortField: 'subject',
        render: (row) => (
          <span className="text-foreground block max-w-[13rem] truncate font-medium sm:max-w-none">{row.subject}</span>
        ),
      },
      {
        key: 'sent_at',
        label: 'Sent',
        width: 170,
        sortField: 'sent_at',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {row.sent_at ? formatDateTime(row.sent_at) : 'Not sent'}
          </span>
        ),
      },
      {
        key: 'recipient_count',
        label: 'Clients',
        width: 90,
        align: 'right',
        sortField: 'recipient_count',
        render: (row) => <span className="tabular-nums">{row.recipient_count}</span>,
      },
      {
        key: 'delivery',
        label: 'What happened',
        width: 220,
        render: (row) => <span className="text-muted-foreground">{deliveryBreakdown(row)}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        phone: true,
        // A broadcast has no kind in the status map (`components/page/status.ts`
        // is 15A's); these are the two tones this screen always drew.
        render: (row) => (
          <Badge tone={row.status === 'sent' ? 'success' : 'neutral'}>
            {DELIVERY_LABELS[row.status === 'sent' ? 'sent' : 'pending']}
          </Badge>
        ),
      },
    ],
    [],
  )

  const list = useListTable(sent, {
    columns,
    search: ['subject'],
    initialSort: { field: 'sent_at', dir: 'desc' },
  })

  if (!allowed) {
    return (
      <div className="w-full min-w-0 space-y-6">
        <PageHeader title="Broadcasts" description="One message to many of your clients at once." />
        <Alert tone="info">
          Composing a broadcast means choosing recipients out of the firm's whole client list, and
          your role sees only your own work.
        </Alert>
      </div>
    )
  }

  const ready = canSend(draft, preview)
  const writeButton = (
    <Button
      onClick={() => {
        setResult(null)
        setDialogError(null)
        setComposing(true)
      }}
    >
      <Megaphone size={16} aria-hidden /> Write a broadcast
    </Button>
  )

  return (
    <>
      <ListPage
        title="Broadcasts"
        description="One message to many of your clients at once — in their portal, and by email if you name a template."
        primary={writeButton}
        loading={loading}
        error={error}
        isEmpty={list.isEmpty && !result}
        empty={{
          title: 'Nothing sent yet',
          description:
            'A broadcast reaches every client who matches a service, a kind or a tag. You see exactly who before it goes.',
          action: writeButton,
        }}
        toolbar={<ListToolbar list={list} placeholder="Search by subject" />}
      >
        {result && <Alert tone="success">{result}</Alert>}
        <ListTable list={list} rowKey={(row) => row.id} />
      </ListPage>

      <ComposeDialog
        open={composing}
        draft={draft}
        onDraft={setDraft}
        preview={preview}
        catalog={catalog}
        resolving={resolving}
        error={dialogError}
        ready={ready}
        onResolve={() => void resolve()}
        onSend={() => {
          setDialogError(null)
          setComposing(false)
          setConfirming(true)
        }}
        onClose={() => setComposing(false)}
      />

      <ConfirmDialog
        open={confirming}
        onClose={() => {
          setConfirming(false)
          setComposing(true)
        }}
        title="Send this broadcast?"
        confirmLabel={preview ? `Send to ${preview.count}` : 'Send'}
        cancelLabel="Not yet"
        onConfirm={() => void send()}
        busy={sending}
        busyLabel="Sending…"
        error={dialogError}
      >
        <p>
          <span className="text-foreground font-medium">{draft.subject}</span> —{' '}
          {audienceSummary(preview, Boolean(draft.templateCode))}
        </p>
        <p>A broadcast cannot be recalled once it is sent.</p>
      </ConfirmDialog>
    </>
  )
}
